import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useClickAway, useSize } from 'ahooks'
import { useRequest } from 'alova/client'
import { observer } from 'mobx-react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import NiceModal from '@ebay/nice-modal-react'
import AIcon from '../A/AIcon/AIcon'
import styles from './NavigatorMenu.module.less'
import {
  getProductDashboardLink,
  PAGE_LINKS,
  ROOT_CONTAINER_ID,
} from '@/router/pageConst'
import { commonUtils } from '@/helpers/commonUtils'
import { corpService } from '@/services/corpService'
import { api } from '@/api'
import { useUserInfo, userService } from '@/services/userService'
import MiddleText from '../MiddleText/MiddleText'
import ProjectCreateBtn from '@/pages/ProjectPage/components/ProjectCreateBtn/ProjectCreateBtn'
import { useTypedLocalStorage } from '@/helpers/commonHooks/useTypedLocalStorage'
import DefaultAvatar from '../DefaultAvatar/DefaultAvatar'
import UserActionPanel from './components/UserActionPanel/UserActionPanel'
import ProductSwitchModal from '../Modals/ProductSwitchModal/ProductSwitchModal'
import {
  getProductMode,
  getProductOrientation,
  isFreeTrialProductCategory,
} from '@/helpers/productCategory'

interface Menu {
  icon?: string
  title: string
  key: string
  children?: Menu[]
  url?: string
  belongUrls?: string[]
  dashboardKey?: 'project' | 'statistic' | 'statisticCompare' | 'account' | 'subscription'
}

type CorpMenuItem = {
  key: string
  name: string
}

function useMenuInitialFocusKey(menus: Menu[]) {
  const { pathname } = useLocation()
  const [focusKey, setFocusKey] = useState<string>('')

  useEffect(() => {
    menus.some((menu) => {
      const menuIs = menu.belongUrls?.includes(pathname)
      const childIs = menu.children?.some((child) => {
        const is = child.url === pathname || child.belongUrls?.includes(pathname)
        if (is) {
          setFocusKey(child.key)
        }
        return is
      })
      if (childIs) {
        return true
      }
      if (menuIs) {
        setFocusKey(menu.key)
      }
      return menuIs || childIs
    })
  }, [pathname, menus])

  return { focusKey }
}

interface Props {
  productType: ProjectModels.ProjectCategory
}

function NavigatorMenu(props: Props) {
  const { productType } = props
  const navigate = useNavigate()
  const { t } = useTranslation()
  const userInfo = useUserInfo()

  const [focusKey, setFocusKey] = useState<string>('')
  const [collapsed, setCollapsed] = useTypedLocalStorage('experience-menu-collapsed', {
    defaultValue: false,
  })
  const [prevCorp, setPrevCorp] = useTypedLocalStorage('prev-selected-corp', {
    defaultValue: undefined,
  })
  const [corpList, setCorpList] = useState<CorpMenuItem[]>([])
  const [currentCorp, setCurrentCorp] = useState<{ key: string; name: string }>()
  const [actionPanelVisible, setActionPanelVisible] = useState(false)
  const userActionAreaRef = useRef<HTMLDivElement>(null)

  const { data: corpData, send: sendCorpList } = useRequest(api.corp.list())
  const rootContainerSize = useSize(document.getElementById(ROOT_CONTAINER_ID))

  const baseMenus: Menu[] = useMemo(() => {
    const allMenus: Menu[] = [
      {
        icon: 'icon-line-global_project',
        title: t('dashboard.layout.projectMenuText'),
        key: 'project',
        dashboardKey: 'project',
      },
      {
        icon: 'icon-line-global_Data',
        title: t('dashboard.layout.dataAnalysis'),
        key: 'dataAnalysis',
        children: [
          {
            title: t('dashboard.layout.dataOverview'),
            key: 'statistic',
            dashboardKey: 'statistic',
          },
          {
            title: t('dashboard.layout.dataComparison'),
            key: 'statisticCompare',
            dashboardKey: 'statisticCompare',
          },
        ],
      },
      {
        icon: 'icon-line-global_manageAccount',
        title: t('dashboard.layout.myAccountText'),
        key: 'account',
        dashboardKey: 'account',
      },
      {
        icon: 'icon-line-global_Subscribe',
        title: t('dashboard.layout.subscriptionText'),
        key: 'subscription',
        dashboardKey: 'subscription',
      },
    ]

    return allMenus
  }, [productType, t])

  const menus: Menu[] = useMemo(() => {
    const bindDashboardLink = (menu: Menu): Menu => {
      if (!menu.dashboardKey) {
        return {
          ...menu,
          children: menu.children?.map(bindDashboardLink),
        }
      }
      const currentLink = getProductDashboardLink(productType, menu.dashboardKey)
      const belongUrls =
        menu.dashboardKey === 'project'
          ? [
              currentLink,
              getProductDashboardLink(productType, 'projectCreate'),
              getProductDashboardLink(productType, 'projectDetail'),
            ]
          : menu.dashboardKey === 'statisticCompare'
            ? [
                currentLink,
                getProductDashboardLink(productType, 'statisticCompareStart'),
                getProductDashboardLink(productType, 'statisticCompareInfo'),
              ]
            : [currentLink]
      return {
        ...menu,
        url: currentLink,
        belongUrls,
        children: menu.children?.map(bindDashboardLink),
      }
    }
    return baseMenus.map(bindDashboardLink)
  }, [baseMenus, productType])

  const { focusKey: initialFocusKey } = useMenuInitialFocusKey(menus)

  const productTypeLabel = (() => {
    const mode = getProductMode(productType)
    const orientation = getProductOrientation(productType)
    if (orientation === 'portrait' && mode === 'quick') {
      return t('productSwitch.modal.cards.portraitQuick.title')
    }
    if (orientation === 'portrait' && mode === 'base') {
      return t('productSwitch.modal.cards.portraitBase.title')
    }
    if (orientation === 'portrait' && mode === 'survey') {
      return t('productSwitch.modal.cards.portraitSurvey.title')
    }
    if (mode === 'survey') {
      return t('productSwitch.modal.cards.tvcSurvey.title')
    }
    if (mode === 'quick') {
      return t('productSwitch.modal.cards.tvcQuick.title')
    }
    return t('productSwitch.modal.cards.tvcBase.title')
  })()
  const currentCorpName = currentCorp?.name || userInfo?.corpName || '-'

  const changeCorp = async (corpId: string, list: CorpMenuItem[] = corpList) => {
    if (corpId === currentCorp?.key) {
      return
    }
    await api.corp.use(corpId).send()
    await userService.refreshUserInfo()
    const corp = list.find((item) => item.key === corpId)
    setCurrentCorp(corp)
    setPrevCorp(corp)
    navigate(getProductDashboardLink(productType, 'project'))
    location.reload()
  }

  const handleProductSwitch = (nextProductType: ProjectModels.ProjectCategory) => {
    if (nextProductType === productType) {
      return
    }
    navigate(getProductDashboardLink(nextProductType, 'project'))
  }

  const handleOpenProductSwitch = () => {
    NiceModal.show(ProductSwitchModal, {
      productType,
      onSelectProduct: handleProductSwitch,
    })
  }

  const handleMenuClick = (menu: Menu) => {
    if (!menu.url) {
      return
    }
    navigate(menu.url, { replace: true })
    setFocusKey(menu.key)
  }

  const handleCollapse = () => {
    setCollapsed(!collapsed)
  }

  useEffect(() => {
    corpService.refreshFreeTrialStatus(productType)
  }, [productType])

  useEffect(() => {
    if (corpData) {
      const nextCorpList: CorpMenuItem[] = corpData.corpList.map((corp) => {
        return {
          key: corp.id,
          name: corp.name,
        }
      })
      setCorpList(nextCorpList)
      const existPrevCorp = prevCorp && nextCorpList.find((corp) => corp.key === prevCorp.key)
      if (existPrevCorp) {
        setCurrentCorp(existPrevCorp)
        // Wait until user info is ready before auto-switching corp.
        // Otherwise it may trigger changeCorp + reload in a loop.
        if (userInfo && userInfo.corpId !== existPrevCorp.key) {
          changeCorp(existPrevCorp.key, nextCorpList)
        }
      } else {
        const currentUserCorp = nextCorpList.find((corp) => corp.key === userInfo?.corpId)
        if (currentUserCorp) {
          setCurrentCorp(currentUserCorp)
          setPrevCorp(currentUserCorp)
        }
      }
    }
  }, [corpData, prevCorp, userInfo])

  useEffect(() => {
    if (userInfo) {
      sendCorpList()
    }
  }, [userInfo?.corpName])

  useEffect(() => {
    setFocusKey(initialFocusKey)
  }, [initialFocusKey])

  useEffect(() => {
    if (rootContainerSize === undefined) {
      return
    }
    if (rootContainerSize.width <= 1440) {
      setCollapsed(true)
    }
  }, [rootContainerSize])

  useEffect(() => {
    setActionPanelVisible(false)
  }, [collapsed])

  useClickAway(() => {
    if (actionPanelVisible) {
      setActionPanelVisible(false)
    }
  }, userActionAreaRef)

  return (
    <div
      data-testid='navigator-menu'
      className={classNames(styles.navigatorMenuBox, styles.experienceMode, {
        [styles.collapsed]: collapsed,
        [styles.actionPanelOpen]: actionPanelVisible,
      })}
    >
      <div className={styles.navigatorMenuBoxContent}>
        <div className={styles.productHeader}>
          <div className={styles.productHeaderMain}>
            <Link to={PAGE_LINKS.index} className={styles.productLogoLink}>
              <img
                data-testid='nav-home-logo-link'
                className={classNames(styles.productLogo, {
                  [styles.collapsed]: collapsed,
                })}
                src={
                  collapsed
                    ? commonUtils.getImagePath('common/adeff-simple-logo.png')
                    : commonUtils.getImagePath('homePage/adeff-logo.png')
                }
                alt=''
              />
            </Link>
            {!collapsed && (
              <div className={styles.productSwitchBox}>
                <span className={styles.productTitle}>{productTypeLabel}</span>
                <button
                  type='button'
                  data-testid='nav-product-switch-btn'
                  className={styles.productSwitchEntry}
                  onClick={handleOpenProductSwitch}
                >
                  <AIcon type='icon-fill-global_function' width={14} height={14} />
                </button>
              </div>
            )}
            {collapsed && (
              <div className={styles.collapsedProductSwitchBox}>
                <button
                  type='button'
                  data-testid='nav-product-switch-btn'
                  className={styles.productSwitchEntry}
                  onClick={handleOpenProductSwitch}
                >
                  <AIcon type='icon-fill-global_function' width={14} height={14} />
                </button>
              </div>
            )}
          </div>
        </div>
        <div className={styles.createBtnBox}>
          <ProjectCreateBtn as={<CreateBtn collapsed={collapsed} productType={productType} />} />
        </div>
        {!collapsed && (
          <ul className={styles.navigatorMenu}>
            {menus.map((menu) => {
              return (
                <MenuGroup
                  key={menu.key}
                  menu={menu}
                  focusKey={focusKey}
                  onMenuClick={handleMenuClick}
                />
              )
            })}
          </ul>
        )}
        {collapsed && (
          <ul className={styles.navigatorMenu}>
            {menus.map((menu) => {
              return (
                <CollapsedMenuGroup
                  key={menu.key}
                  menu={menu}
                  focusKey={focusKey}
                  onMenuClick={handleMenuClick}
                />
              )
            })}
          </ul>
        )}
        <div className={styles.footerBox}>
          {userInfo && (
            <div className={styles.userActionArea} ref={userActionAreaRef}>
              {actionPanelVisible && (
                <UserActionPanel
                  className={styles.userActionPanelAnchor}
                  corpList={corpList}
                  currentCorpKey={currentCorp?.key}
                  currentCorpName={currentCorpName}
                  onChangeCorp={(corpId) => changeCorp(corpId)}
                  onLogout={() => userService.logout()}
                />
              )}
              <div
                data-testid='nav-user-card'
                className={styles.userCard}
                onClick={() => {
                  if (collapsed) {
                    setActionPanelVisible(!actionPanelVisible)
                  }
                }}
              >
                <div className={styles.userMain}>
                  <DefaultAvatar
                    className={styles.userAvatar}
                    avatar={userInfo.avatar}
                    name={userInfo.name}
                  />
                  {!collapsed && (
                    <div className={styles.userMeta}>
                      <p className={styles.userName}>{userInfo.name}</p>
                      <p className={styles.corpName}>{currentCorpName}</p>
                    </div>
                  )}
                </div>
                {!collapsed && (
                  <button
                    type='button'
                    data-testid='nav-user-action-btn'
                    className={styles.moreActionButton}
                    onClick={() => setActionPanelVisible(!actionPanelVisible)}
                  >
                    <AIcon type='icon-fill-global_more' width={17} height={17} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <CollapseButton collapsed={collapsed} onClick={handleCollapse} />
    </div>
  )
}

function CollapsedMenuGroup(props: {
  menu: Menu
  focusKey: string
  onMenuClick: (menu: Menu) => void
}) {
  const { menu, focusKey, onMenuClick } = props
  const children = menu.children || []
  const childFocused = children.some((child) => child.key === focusKey)

  return (
    <li
      className={classNames(styles.navigatorMenuGroup, styles.collapsed, {
        [styles.childFocus]: childFocused,
      })}
    >
      <div
        data-testid={`nav-${menu.key}-link`}
        className={classNames(styles.navigatorMenuItem, styles.collapsed, {
          [styles.hasChild]: children.length > 0,
          [styles.focus]: focusKey === menu.key || childFocused,
        })}
        onClick={() => onMenuClick(menu)}
      >
        <span className={styles.navigatorMenuItemIcon}>
          {menu.icon && <AIcon width={22} height={22} type={menu.icon}></AIcon>}
        </span>
      </div>
      {children.length > 0 && (
        <div className={styles.collapsedSubMenuPanel}>
          <div className={styles.collapsedSubMenuTitle}>{menu.title}</div>
          <ul className={styles.collapsedSubMenu}>
            {children.map((child) => {
              return (
                <li
                  key={child.key}
                  data-testid={`nav-${child.key}-link`}
                  className={classNames(styles.collapsedSubMenuItem, {
                    [styles.focus]: focusKey === child.key,
                  })}
                  onClick={() => onMenuClick(child)}
                >
                  {child.title}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </li>
  )
}

function MenuGroup(props: {
  menu: Menu
  focusKey: string
  onMenuClick: (menu: Menu) => void
}) {
  const { menu, focusKey, onMenuClick } = props
  const children = menu.children || []
  const childFocused = children.some((child) => child.key === focusKey)

  return (
    <li className={styles.navigatorMenuGroup}>
      <div
        data-testid={`nav-${menu.key}-link`}
        className={classNames(styles.navigatorMenuItem, {
          [styles.hasChild]: children.length > 0,
          [styles.focus]: focusKey === menu.key,
          [styles.childFocus]: childFocused,
        })}
        onClick={() => onMenuClick(menu)}
      >
        <span className={styles.navigatorMenuItemIcon}>
          {menu.icon && <AIcon width={22} height={22} type={menu.icon}></AIcon>}
        </span>
        <span className={styles.navigatorMenuItemTitle}>{menu.title}</span>
      </div>
      {children.length > 0 && (
        <ul className={styles.navigatorSubMenu}>
          {children.map((child) => {
            return (
              <li
                key={child.key}
                data-testid={`nav-${child.key}-link`}
                className={classNames(styles.navigatorSubMenuItem, {
                  [styles.focus]: focusKey === child.key,
                })}
                onClick={() => onMenuClick(child)}
              >
                <span className={styles.navigatorSubMenuItemTitle}>{child.title}</span>
              </li>
            )
          })}
        </ul>
      )}
    </li>
  )
}

function CollapseButton(props: { collapsed?: boolean; onClick: () => void }) {
  return (
    <span
      data-testid='nav-collapse-btn'
      className={classNames(styles.collapseButton, { [styles.collapsed]: props.collapsed })}
      onClick={props.onClick}
    >
      <AIcon type='icon-fill-global_play2' width={10} height={10} />
    </span>
  )
}

const CreateBtn = observer((props: { collapsed?: boolean; productType: ProjectModels.ProjectCategory }) => {
  const { t } = useTranslation()
  const trialStatus = corpService.getFreeTrialStatus(props.productType)
  const prevLoadedCanUseFreeTrialRef = useRef(false)
  const canUseFreeTrial =
    isFreeTrialProductCategory(props.productType) &&
    trialStatus.freeTrial
  const displayCanUseFreeTrial = trialStatus.loaded
    ? canUseFreeTrial
    : prevLoadedCanUseFreeTrialRef.current

  useEffect(() => {
    if (trialStatus.loaded) {
      prevLoadedCanUseFreeTrialRef.current = canUseFreeTrial
    }
  }, [canUseFreeTrial, trialStatus.loaded])

  const text = (
    <span>
      {displayCanUseFreeTrial ? (
        <MiddleText text={t('modals.freeTrialModal.title1')} />
      ) : (
        <MiddleText text={t('dashboard.projectPage.createProject')} />
      )}
    </span>
  )
  return (
    <div
      className={classNames(styles.createBtn, { [styles.collapsed]: props.collapsed })}
      data-testid='nav-create-project-btn'
    >
      <AIcon type='icon-line-global_add' width={16} height={16} />
      {props.collapsed ? null : text}
    </div>
  )
})

export default observer(NavigatorMenu)
